import { weekFileName } from '../../lib/weekFileName';
import { DocLangToggle } from '../ui/doc-lang-toggle';
import { useTranslation } from 'react-i18next';
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { 
  FileText, Edit, Save, X, Calendar, User, Clock, 
  History, Link2, FileSignature, FileDown, AlertTriangle, Camera
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import EntityLinker from './EntityLinker';
import ElectronicSignaturePanel from './ElectronicSignature';
import EntityPhotoGallery from '../EntityPhotoGallery';
import { DateText } from '../ui/week-badge';
import { fmtDateTimeWeek } from '../../lib/dateFormat';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Document Detail Dialog
export const DocumentDetailDialog = ({ 
  open, 
  onOpenChange, 
  document, 
  employees, 
  projects, 
  token, 
  onSave, 
  onRefresh,
  docCategories = []
}) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('details');

  useEffect(() => {
    if (document) {
      setFormData({
        title: document.title || '',
        description: document.description || '',
        category: document.category || 'procedure',
        access_level: document.access_level || 'all',
        reviewer_id: document.reviewer_id || '',
        approver_id: document.approver_id || '',
        review_frequency_months: document.review_frequency_months || 12,
        linked_project_ids: document.linked_project_ids || [],
        linked_employee_ids: document.linked_employee_ids || [],
      });
    }
  }, [document]);

  const handleSave = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/iso/documents/${document.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) throw new Error('Failed to update document');
      
      toast.success(t('auto.iso.documentUpdatedSuccessfully'));
      setIsEditing(false);
      onRefresh?.();
      onSave?.();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleExportPdf = async () => {
    try {
      toast.info(t('auto.iso.generatingPdf'));
      const response = await fetch(`${API_BASE}/api/iso/documents/${document.id}/pdf`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      if (!response.ok) throw new Error('Failed to generate PDF');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = window.document.createElement('a');
      a.href = url;
      a.download = weekFileName(`Document_${document.document_number}.pdf`);
      window.document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      window.document.body.removeChild(a);
      
      toast.success(t('auto.iso.pdfExportedSuccessfully'));
    } catch (error) {
      toast.error(error.message);
    }
  };

  if (!document) return null;

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { color: 'bg-gray-100 text-gray-800', label: t('auto.lbl.draft') },
      pending_review: { color: 'bg-yellow-100 text-yellow-800', label: t('auto.lbl.pendingReview') },
      pending_approval: { color: 'bg-blue-100 text-blue-800', label: t('auto.lbl.pendingApproval') },
      approved: { color: 'bg-green-100 text-green-800', label: t('auto.lbl.approved') },
      obsolete: { color: 'bg-red-100 text-red-800', label: t('auto.lbl.obsolete') },
    };
    const config = statusConfig[status] || statusConfig.draft;
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" aria-describedby="document-dialog-description">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FileText className="h-5 w-5 text-blue-600" />
              <div>
                <DialogTitle>{document.document_number}</DialogTitle>
                <p className="text-sm text-gray-500">{document.title}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {getStatusBadge(document.status)}
              <Badge variant="outline">v{document.current_version}</Badge>
            </div>
          </div>
        </DialogHeader>
        <DialogDescription id="document-dialog-description" className="sr-only">
          {t('auto.iso.viewAndManageDocumentDetailsHistory')}
        </DialogDescription>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="flex flex-wrap h-auto gap-1 p-1">
            <TabsTrigger value="details" data-testid="tab-details">{t('auto.iso.details')}</TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-history">{t('auto.iso.history')}</TabsTrigger>
            <TabsTrigger value="signatures" data-testid="tab-signatures">{t('auto.iso.signatures')}</TabsTrigger>
          </TabsList>

          <TabsContent value="details" className="space-y-4">
            {isEditing ? (
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium">{t('auto.iso.title2')}</label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value  }))}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">{t('auto.iso.description')}</label>
                  <textarea
                    className="w-full min-h-[80px] p-2 border rounded-md text-sm"
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value  }))}
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">{t('auto.iso.category')}</label>
                    <Select
                      value={formData.category}
                      onValueChange={(v) => setFormData(prev => ({ ...prev, category: v  }))}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {docCategories.map(cat => (
                          <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium">{t('auto.iso.accessLevel')}</label>
                    <Select
                      value={formData.access_level}
                      onValueChange={(v) => setFormData(prev => ({ ...prev, access_level: v  }))}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('auto.iso.allUsers')}</SelectItem>
                        <SelectItem value="managers">{t('auto.iso.managersOnly')}</SelectItem>
                        <SelectItem value="admins">{t('auto.iso.adminsOnly')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">{t('auto.iso.reviewer')}</label>
                    <Select
                      value={formData.reviewer_id}
                      onValueChange={(v) => setFormData(prev => ({ ...prev, reviewer_id: v  }))}
                    >
                      <SelectTrigger><SelectValue placeholder={t('auto.iso.selectReviewer')} /></SelectTrigger>
                      <SelectContent>
                        {employees.map(emp => (
                          <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium">{t('auto.iso.approver')}</label>
                    <Select
                      value={formData.approver_id}
                      onValueChange={(v) => setFormData(prev => ({ ...prev, approver_id: v  }))}
                    >
                      <SelectTrigger><SelectValue placeholder={t('auto.iso.selectApprover')} /></SelectTrigger>
                      <SelectContent>
                        {employees.map(emp => (
                          <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium">{t('auto.iso.reviewFrequencyMonths')}</label>
                  <Input
                    type="number"
                    value={formData.review_frequency_months}
                    onChange={(e) => setFormData(prev => ({ ...prev, review_frequency_months: parseInt(e.target.value) || 12  }))}
                  />
                </div>
                <EntityLinker
                  linkedProjectIds={formData.linked_project_ids}
                  linkedEmployeeIds={formData.linked_employee_ids}
                  projects={projects}
                  employees={employees}
                  onProjectsChange={(ids) => setFormData(prev => ({ ...prev, linked_project_ids: ids  }))}
                  onEmployeesChange={(ids) => setFormData(prev => ({ ...prev, linked_employee_ids: ids  }))}
                />
              </div>
            ) : (
              <div className="space-y-4">
                <Card>
                  <CardContent className="p-4 space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-500">{t('auto.iso.category')}</p>
                        <p className="font-medium">{docCategories.find(c => c.value === document.category)?.label || document.category?.replace('_', ' ')}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">{t('auto.iso.accessLevel')}</p>
                        <p className="font-medium capitalize">{document.access_level === 'all' ? 'All Users' : document.access_level === 'managers' ? 'Managers Only' : document.access_level === 'admins' ? 'Admins Only' : document.access_level}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">{t('auto.iso.reviewer')}</p>
                        <p className="font-medium">{document.reviewer_id ? (employees.find(e => e.id === document.reviewer_id)?.name || 'Unknown') : 'Not assigned'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">{t('auto.iso.approver')}</p>
                        <p className="font-medium">{document.approver_id ? (employees.find(e => e.id === document.approver_id)?.name || 'Unknown') : 'Not assigned'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">{t('auto.iso.reviewFrequency')}</p>
                        <p className="font-medium">{document.review_frequency_months} months</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">{t('auto.iso.nextReview')}</p>
                        <p className="font-medium">
                          {document.next_review_date 
                            ? <DateText value={document.next_review_date} />
                            : 'Not set'}
                        </p>
                      </div>
                    </div>
                    {document.description && (
                      <div>
                        <p className="text-sm text-gray-500">{t('auto.iso.description')}</p>
                        <p className="text-sm">{document.description}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Link2 className="h-4 w-4" />
                      {t('auto.iso.linkedEntities')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <EntityLinker
                      linkedProjectIds={document.linked_project_ids || []}
                      linkedEmployeeIds={document.linked_employee_ids || []}
                      projects={projects}
                      employees={employees}
                      readOnly={true}
                    />
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          <TabsContent value="history">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <History className="h-4 w-4" />
                  {t('auto.iso.versionHistory')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {document.versions?.length > 0 ? (
                  <div className="space-y-2">
                    {document.versions.map((version, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 border rounded">
                        <div>
                          <p className="font-medium">Version {version.version}</p>
                          <p className="text-xs text-gray-500">
                            {version.uploaded_at && fmtDateTimeWeek(version.uploaded_at)}
                          </p>
                        </div>
                        <Badge variant="outline">{version.filename}</Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 text-center py-4">{t('auto.iso.noVersionsUploadedYet')}</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="signatures">
            <ElectronicSignaturePanel
              documentType="document"
              documentId={document.id}
              documentTitle={document.title}
              signatures={document.signatures || []}
              token={token}
              onSignatureAdded={onRefresh}
            />
          </TabsContent>
        </Tabs>

        <DialogFooter className="flex justify-between">
          <div className="flex gap-2">
            <DocLangToggle testId="doc-lang-isodetaildialogs-0" />
            <Button variant="outline" onClick={handleExportPdf} data-testid="dialog-exportpdf-btn">
              <FileDown className="h-4 w-4 mr-2" />
              {t('auto.iso.exportPdf')}
            </Button>
          </div>
          <div className="flex gap-2">
            {isEditing ? (
              <>
                <Button variant="outline" onClick={() => setIsEditing(false)} data-testid="dialog-cancel-btn">{t('auto.iso.cancel')}</Button>
                <Button onClick={handleSave} disabled={isLoading} data-testid="dialog-save-btn">
                  <Save className="h-4 w-4 mr-2" />
                  {isLoading ? t('auto.iso.saving') : t('auto.iso.save')}
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => setIsEditing(true)} data-testid="dialog-isodetaildialogs-outline-btn">
                  <Edit className="h-4 w-4 mr-2" />
                  {t('auto.iso.edit')}
                </Button>
                <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="dialog-close-btn">{t('auto.iso.close')}</Button>
              </>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// NCR Detail Dialog
export const NCRDetailDialog = ({ 
  open, 
  onOpenChange, 
  ncr, 
  employees, 
  projects, 
  token, 
  onSave, 
  onRefresh 
}) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (ncr) {
      setFormData({
        title: ncr.title || '',
        description: ncr.description || '',
        severity: ncr.severity || 'minor',
        department: ncr.department || '',
        root_cause: ncr.root_cause || '',
        immediate_action: ncr.immediate_action || '',
        linked_project_ids: ncr.linked_project_ids || [],
        linked_employee_ids: ncr.linked_employee_ids || [],
      });
    }
  }, [ncr]);

  const handleSave = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/iso/ncr/${ncr.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) throw new Error('Failed to update NCR');
      
      toast.success(t('auto.iso.ncrUpdatedSuccessfully'));
      setIsEditing(false);
      onRefresh?.();
      onSave?.();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleExportPdf = async (withPhotos = false) => {
    try {
      toast.info(withPhotos ? t('auto.iso.generatingPdfWithPhotos') : t('auto.iso.generatingPdf'));
      const qs = withPhotos ? '?with_photos=true' : '';
      const response = await fetch(`${API_BASE}/api/iso/ncr/${ncr.id}/pdf${qs}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      if (!response.ok) throw new Error('Failed to generate PDF');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = window.document.createElement('a');
      a.href = url;
      a.download = weekFileName(`NCR_${ncr.ncr_number}${withPhotos ? '_with_photos' : ''}.pdf`);
      window.document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      window.document.body.removeChild(a);
      
      toast.success(t('auto.iso.pdfExportedSuccessfully'));
    } catch (error) {
      toast.error(error.message);
    }
  };

  if (!ncr) return null;

  const getSeverityBadge = (severity) => {
    const config = {
      critical: { color: 'bg-red-100 text-red-800', label: t('auto.lbl.critical') },
      major: { color: 'bg-orange-100 text-orange-800', label: t('auto.lbl.major') },
      minor: { color: 'bg-yellow-100 text-yellow-800', label: t('auto.lbl.minor') },
    };
    const c = config[severity] || config.minor;
    return <Badge className={c.color}>{c.label}</Badge>;
  };

  const getStatusBadge = (status) => {
    const config = {
      open: { color: 'bg-yellow-100 text-yellow-800', label: t('auto.lbl.open') },
      investigating: { color: 'bg-blue-100 text-blue-800', label: t('auto.lbl.investigating') },
      action_required: { color: 'bg-orange-100 text-orange-800', label: t('auto.lbl.actionRequired') },
      verification: { color: 'bg-purple-100 text-purple-800', label: t('auto.lbl.verification') },
      closed: { color: 'bg-green-100 text-green-800', label: t('auto.lbl.closed') },
    };
    const c = config[status] || config.open;
    return <Badge className={c.color}>{c.label}</Badge>;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" aria-describedby="ncr-dialog-description">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-yellow-600" />
              <div>
                <DialogTitle>{ncr.ncr_number}</DialogTitle>
                <p className="text-sm text-gray-500">{ncr.title}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {getSeverityBadge(ncr.severity)}
              {getStatusBadge(ncr.status)}
            </div>
          </div>
        </DialogHeader>
        <DialogDescription id="ncr-dialog-description" className="sr-only">
          {t('auto.iso.nonConformanceReportDetails')}
        </DialogDescription>

        <div className="space-y-4">
          {isEditing ? (
            <>
              <div>
                <label className="text-sm font-medium">{t('auto.iso.title2')}</label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value  }))}
                />
              </div>
              <div>
                <label className="text-sm font-medium">{t('auto.iso.description')}</label>
                <textarea
                  className="w-full min-h-[80px] p-2 border rounded-md text-sm"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value  }))}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">{t('auto.iso.severity')}</label>
                  <Select
                    value={formData.severity}
                    onValueChange={(v) => setFormData(prev => ({ ...prev, severity: v  }))}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="minor">{t('auto.iso.minor')}</SelectItem>
                      <SelectItem value="major">{t('auto.iso.major')}</SelectItem>
                      <SelectItem value="critical">{t('auto.iso.critical')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium">{t('auto.iso.department')}</label>
                  <Input
                    value={formData.department}
                    onChange={(e) => setFormData(prev => ({ ...prev, department: e.target.value  }))}
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">{t('auto.iso.rootCause')}</label>
                <textarea
                  className="w-full min-h-[60px] p-2 border rounded-md text-sm"
                  value={formData.root_cause}
                  onChange={(e) => setFormData(prev => ({ ...prev, root_cause: e.target.value  }))}
                />
              </div>
              <div>
                <label className="text-sm font-medium">{t('auto.iso.immediateAction')}</label>
                <textarea
                  className="w-full min-h-[60px] p-2 border rounded-md text-sm"
                  value={formData.immediate_action}
                  onChange={(e) => setFormData(prev => ({ ...prev, immediate_action: e.target.value  }))}
                />
              </div>
              <EntityLinker
                linkedProjectIds={formData.linked_project_ids}
                linkedEmployeeIds={formData.linked_employee_ids}
                projects={projects}
                employees={employees}
                onProjectsChange={(ids) => setFormData(prev => ({ ...prev, linked_project_ids: ids  }))}
                onEmployeesChange={(ids) => setFormData(prev => ({ ...prev, linked_employee_ids: ids  }))}
              />
            </>
          ) : (
            <>
              <Card>
                <CardContent className="p-4 space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">{t('auto.iso.detectedDate')}</p>
                      <p className="font-medium">
                        {ncr.detected_date 
                          ? <DateText value={ncr.detected_date} />
                          : 'Not set'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">{t('auto.iso.department')}</p>
                      <p className="font-medium">{ncr.department || '-'}</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{t('auto.iso.description')}</p>
                    <p className="text-sm">{ncr.description}</p>
                  </div>
                  {ncr.root_cause && (
                    <div>
                      <p className="text-sm text-gray-500">{t('auto.iso.rootCause')}</p>
                      <p className="text-sm">{ncr.root_cause}</p>
                    </div>
                  )}
                  {ncr.immediate_action && (
                    <div>
                      <p className="text-sm text-gray-500">{t('auto.iso.immediateAction')}</p>
                      <p className="text-sm">{ncr.immediate_action}</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Photo evidence */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Camera className="h-4 w-4" />
                    {t('auto.iso.photoEvidence')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <EntityPhotoGallery
                    entityType="ncr"
                    entityId={ncr.id}
                    itemKey={null}
                  />
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Link2 className="h-4 w-4" />
                    {t('auto.iso.linkedEntities')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <EntityLinker
                    linkedProjectIds={ncr.linked_project_ids || []}
                    linkedEmployeeIds={ncr.linked_employee_ids || []}
                    projects={projects}
                    employees={employees}
                    readOnly={true}
                  />
                </CardContent>
              </Card>

              <ElectronicSignaturePanel
                documentType="ncr"
                documentId={ncr.id}
                documentTitle={ncr.title}
                signatures={ncr.signatures || []}
                token={token}
                onSignatureAdded={onRefresh}
              />
            </>
          )}
        </div>

        <DialogFooter className="flex justify-between">
          <div className="flex gap-2">
            <DocLangToggle testId="doc-lang-isodetaildialogs-1" />
            <Button variant="outline" onClick={handleExportPdf} data-testid="dialog-exportpdf-btn">
              <FileDown className="h-4 w-4 mr-2" />
              {t('auto.iso.exportPdf')}
            </Button>
            <Button variant="outline" onClick={() => handleExportPdf(true)} data-testid="ncr-export-pdf-with-photos">
              <Camera className="h-4 w-4 mr-2" />
              PDF + Photo Evidence
            </Button>
          </div>
          <div className="flex gap-2">
            {isEditing ? (
              <>
                <Button variant="outline" onClick={() => setIsEditing(false)} data-testid="dialog-cancel-btn">{t('auto.iso.cancel')}</Button>
                <Button onClick={handleSave} disabled={isLoading} data-testid="dialog-save-btn">
                  <Save className="h-4 w-4 mr-2" />
                  {isLoading ? t('auto.iso.saving') : t('auto.iso.save')}
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => setIsEditing(true)} data-testid="dialog-isodetaildialogs-outline-btn">
                  <Edit className="h-4 w-4 mr-2" />
                  {t('auto.iso.edit')}
                </Button>
                <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="dialog-close-btn">{t('auto.iso.close')}</Button>
              </>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// CAPA Detail Dialog
export const CAPADetailDialog = ({ 
  open, 
  onOpenChange, 
  capa, 
  employees, 
  projects, 
  token, 
  onSave, 
  onRefresh 
}) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (capa) {
      setFormData({
        title: capa.title || '',
        description: capa.description || '',
        priority: capa.priority || 'medium',
        corrective_action: capa.corrective_action || '',
        preventive_action: capa.preventive_action || '',
        linked_project_ids: capa.linked_project_ids || [],
      });
    }
  }, [capa]);

  const handleSave = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/iso/capa/${capa.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) throw new Error('Failed to update CAPA');
      
      toast.success(t('auto.iso.capaUpdatedSuccessfully'));
      setIsEditing(false);
      onRefresh?.();
      onSave?.();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleExportPdf = async () => {
    try {
      toast.info(t('auto.iso.generatingPdf'));
      const response = await fetch(`${API_BASE}/api/iso/capa/${capa.id}/pdf`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      if (!response.ok) throw new Error('Failed to generate PDF');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = window.document.createElement('a');
      a.href = url;
      a.download = weekFileName(`CAPA_${capa.capa_number}.pdf`);
      window.document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      window.document.body.removeChild(a);
      
      toast.success(t('auto.iso.pdfExportedSuccessfully'));
    } catch (error) {
      toast.error(error.message);
    }
  };

  if (!capa) return null;

  const getPriorityBadge = (priority) => {
    const config = {
      critical: { color: 'bg-red-100 text-red-800', label: t('auto.lbl.critical') },
      high: { color: 'bg-orange-100 text-orange-800', label: t('auto.lbl.high') },
      medium: { color: 'bg-yellow-100 text-yellow-800', label: t('auto.lbl.medium') },
      low: { color: 'bg-green-100 text-green-800', label: t('auto.lbl.low') },
    };
    const c = config[priority] || config.medium;
    return <Badge className={c.color}>{c.label}</Badge>;
  };

  const getStatusBadge = (status) => {
    const config = {
      open: { color: 'bg-yellow-100 text-yellow-800', label: t('auto.lbl.open') },
      planning: { color: 'bg-blue-100 text-blue-800', label: t('auto.lbl.planning') },
      implementation: { color: 'bg-purple-100 text-purple-800', label: t('auto.lbl.implementation') },
      verification: { color: 'bg-orange-100 text-orange-800', label: t('auto.lbl.verification') },
      closed: { color: 'bg-green-100 text-green-800', label: t('auto.lbl.closed') },
    };
    const c = config[status] || config.open;
    return <Badge className={c.color}>{c.label}</Badge>;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" aria-describedby="capa-dialog-description">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FileText className="h-5 w-5 text-green-600" />
              <div>
                <DialogTitle>{capa.capa_number}</DialogTitle>
                <p className="text-sm text-gray-500">{capa.title}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {getPriorityBadge(capa.priority)}
              {getStatusBadge(capa.status)}
            </div>
          </div>
        </DialogHeader>
        <DialogDescription id="capa-dialog-description" className="sr-only">
          {t('auto.iso.correctiveAndPreventiveActionDetails')}
        </DialogDescription>

        <div className="space-y-4">
          {isEditing ? (
            <>
              <div>
                <label className="text-sm font-medium">{t('auto.iso.title2')}</label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value  }))}
                />
              </div>
              <div>
                <label className="text-sm font-medium">{t('auto.iso.description')}</label>
                <textarea
                  className="w-full min-h-[80px] p-2 border rounded-md text-sm"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value  }))}
                />
              </div>
              <div>
                <label className="text-sm font-medium">{t('auto.iso.priority')}</label>
                <Select
                  value={formData.priority}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, priority: v  }))}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">{t('auto.iso.low')}</SelectItem>
                    <SelectItem value="medium">{t('auto.iso.medium')}</SelectItem>
                    <SelectItem value="high">{t('auto.iso.high')}</SelectItem>
                    <SelectItem value="critical">{t('auto.iso.critical')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">{t('auto.iso.correctiveAction')}</label>
                <textarea
                  className="w-full min-h-[60px] p-2 border rounded-md text-sm"
                  value={formData.corrective_action}
                  onChange={(e) => setFormData(prev => ({ ...prev, corrective_action: e.target.value  }))}
                />
              </div>
              <div>
                <label className="text-sm font-medium">{t('auto.iso.preventiveAction')}</label>
                <textarea
                  className="w-full min-h-[60px] p-2 border rounded-md text-sm"
                  value={formData.preventive_action}
                  onChange={(e) => setFormData(prev => ({ ...prev, preventive_action: e.target.value  }))}
                />
              </div>
              <EntityLinker
                linkedProjectIds={formData.linked_project_ids}
                linkedEmployeeIds={[]}
                projects={projects}
                employees={employees}
                onProjectsChange={(ids) => setFormData(prev => ({ ...prev, linked_project_ids: ids  }))}
                onEmployeesChange={() => {}}
              />
            </>
          ) : (
            <>
              <Card>
                <CardContent className="p-4 space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">{t('auto.iso.source')}</p>
                      <p className="font-medium">{capa.source?.replace('_', ' ') || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">{t('auto.iso.dueDate')}</p>
                      <p className="font-medium">
                        {capa.due_date 
                          ? <DateText value={capa.due_date} />
                          : 'Not set'}
                      </p>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{t('auto.iso.description')}</p>
                    <p className="text-sm">{capa.description}</p>
                  </div>
                  {capa.corrective_action && (
                    <div>
                      <p className="text-sm text-gray-500">{t('auto.iso.correctiveAction')}</p>
                      <p className="text-sm">{capa.corrective_action}</p>
                    </div>
                  )}
                  {capa.preventive_action && (
                    <div>
                      <p className="text-sm text-gray-500">{t('auto.iso.preventiveAction')}</p>
                      <p className="text-sm">{capa.preventive_action}</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              <ElectronicSignaturePanel
                documentType="capa"
                documentId={capa.id}
                documentTitle={capa.title}
                signatures={capa.signatures || []}
                token={token}
                onSignatureAdded={onRefresh}
              />
            </>
          )}
        </div>

        <DialogFooter className="flex justify-between">
          <div className="flex gap-2">
            <DocLangToggle testId="doc-lang-isodetaildialogs-2" />
            <Button variant="outline" onClick={handleExportPdf} data-testid="dialog-exportpdf-btn">
              <FileDown className="h-4 w-4 mr-2" />
              {t('auto.iso.exportPdf')}
            </Button>
          </div>
          <div className="flex gap-2">
            {isEditing ? (
              <>
                <Button variant="outline" onClick={() => setIsEditing(false)} data-testid="dialog-cancel-btn">{t('auto.iso.cancel')}</Button>
                <Button onClick={handleSave} disabled={isLoading} data-testid="dialog-save-btn">
                  <Save className="h-4 w-4 mr-2" />
                  {isLoading ? t('auto.iso.saving') : t('auto.iso.save')}
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => setIsEditing(true)} data-testid="dialog-isodetaildialogs-outline-btn">
                  <Edit className="h-4 w-4 mr-2" />
                  {t('auto.iso.edit')}
                </Button>
                <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="dialog-close-btn">{t('auto.iso.close')}</Button>
              </>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Training Record Detail Dialog
export const TrainingDetailDialog = ({ 
  open, 
  onOpenChange, 
  training, 
  employees, 
  projects, 
  token, 
  onSave, 
  onRefresh 
}) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [participantResults, setParticipantResults] = useState({});
  const [isUpdatingParticipant, setIsUpdatingParticipant] = useState(null);

  useEffect(() => {
    if (training) {
      setFormData({
        title: training.title || '',
        description: training.description || '',
        training_type: training.training_type || 'internal',
        provider: training.provider || '',
        duration_hours: training.duration_hours || 1,
        pass_criteria: training.pass_criteria || '',
        passing_score: training.passing_score || 80,
        certification_required: training.certification_required || false,
        certification_name: training.certification_name || '',
        linked_project_ids: training.linked_project_ids || [],
      });
      setSelectedEmployees(training.employee_ids || []);
      setParticipantResults(training.participant_results || {});
    }
  }, [training]);

  const handleSave = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/iso/training/${training.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...formData,
          employee_ids: selectedEmployees,
        }),
      });

      if (!response.ok) throw new Error('Failed to update training record');
      
      toast.success(t('auto.iso.trainingRecordUpdatedSuccessfully'));
      setIsEditing(false);
      onRefresh?.();
      onSave?.();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateParticipantStatus = async (empId, status, score = null) => {
    setIsUpdatingParticipant(empId);
    try {
      const body = { status };
      if (score !== null) body.score = parseFloat(score);
      // Auto-stamp now() only if no prior completed_date exists. Any explicit
      // per-participant date set via the date picker is preserved by the
      // backend (merge semantics).
      const existingDate = participantResults[empId]?.completed_date;
      if ((status === 'passed' || status === 'failed') && !existingDate) {
        body.completed_date = new Date().toISOString();
      }

      const response = await fetch(`${API_BASE}/api/iso/training/${training.id}/participant/${empId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) throw new Error('Failed to update participant status');
      
      // Update local state
      setParticipantResults(prev => ({
        ...prev,
        [empId]: {
          ...prev[empId],
          status,
          score: score !== null ? parseFloat(score) : prev[empId]?.score,
          completed_date: body.completed_date || prev[empId]?.completed_date,
        }
      }));
      
      toast.success(t('auto.iso.participantStatusUpdated'));
      onRefresh?.();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsUpdatingParticipant(null);
    }
  };

  const handleUpdateParticipantCompletedDate = async (empId, dateStr) => {
    setIsUpdatingParticipant(empId);
    try {
      const existing = participantResults[empId] || {};
      const body = {
        status: existing.status || 'enrolled',
        completed_date: dateStr ? new Date(`${dateStr}T00:00:00Z`).toISOString() : null,
      };

      const response = await fetch(`${API_BASE}/api/iso/training/${training.id}/participant/${empId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) throw new Error('Failed to update completion date');

      setParticipantResults(prev => ({
        ...prev,
        [empId]: { ...prev[empId], completed_date: body.completed_date },
      }));

      toast.success(t('auto.iso.completionDateUpdated'));
      onRefresh?.();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsUpdatingParticipant(null);
    }
  };

  const handleMarkAllAttended = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/iso/training/${training.id}/mark-all-attended`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) throw new Error('Failed to mark all attended');
      
      const data = await response.json().catch(() => ({}));
      if (data?.participant_results) {
        setParticipantResults(data.participant_results);
      } else {
        // Fallback: patch locally so UI refreshes immediately
        setParticipantResults(prev => {
          const next = { ...prev };
          (training.employee_ids || []).forEach(empId => {
            next[empId] = { ...(next[empId] || {}), status: 'attended' };
          });
          return next;
        });
      }
      
      toast.success(t('auto.iso.allParticipantsMarkedAsAttended'));
      onRefresh?.();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleMarkAllPassed = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/iso/training/${training.id}/mark-all-passed`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) throw new Error('Failed to mark all passed');

      const data = await response.json().catch(() => ({}));
      if (data?.participant_results) {
        setParticipantResults(data.participant_results);
      } else {
        const nowIso = new Date().toISOString();
        setParticipantResults(prev => {
          const next = { ...prev };
          (training.employee_ids || []).forEach(empId => {
            const cur = next[empId] || {};
            // Preserve any manually-set completed_date
            next[empId] = {
              ...cur,
              status: 'passed',
              completed_date: cur.completed_date || nowIso,
            };
          });
          return next;
        });
      }

      toast.success(t('auto.iso.allParticipantsMarkedAsPassed'));
      onRefresh?.();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleIssueAllCertificates = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/iso/training/${training.id}/issue-all-certificates`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) throw new Error('Failed to issue certificates');

      const data = await response.json().catch(() => ({}));
      if (data?.participant_results) {
        setParticipantResults(data.participant_results);
      } else {
        setParticipantResults(prev => {
          const next = { ...prev };
          Object.keys(next).forEach(empId => {
            if (next[empId]?.status === 'passed') {
              next[empId] = { ...next[empId], certificate_issued: true };
            }
          });
          return next;
        });
      }

      const issued = typeof data?.issued_count === 'number' ? data.issued_count : null;
      toast.success(issued !== null ? `Issued ${issued} certificate(s)` : 'Certificates issued');
      onRefresh?.();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleIssueCertificate = async (empId) => {
    setIsUpdatingParticipant(empId);
    try {
      const response = await fetch(`${API_BASE}/api/iso/training/${training.id}/participant/${empId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ 
          status: participantResults[empId]?.status || 'passed',
          certificate_issued: true 
        }),
      });

      if (!response.ok) throw new Error('Failed to issue certificate');
      
      setParticipantResults(prev => ({
        ...prev,
        [empId]: { ...prev[empId], certificate_issued: true }
      }));
      
      toast.success(t('auto.iso.certificateIssued'));
      onRefresh?.();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsUpdatingParticipant(null);
    }
  };

  const handleGenerateCertificatesPdf = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(
        `${API_BASE}/api/iso/training/${training.id}/certificates/pdf`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (!response.ok) {
        let msg = 'Failed to generate certificates PDF';
        try {
          const err = await response.json();
          if (err?.detail) msg = err.detail;
        } catch (_) { /* ignore */ }
        throw new Error(msg);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safeNum = (training.training_number || training.id).replace(/\//g, '-');
      a.download = weekFileName(`Certificates_${safeNum}.pdf`);
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      toast.success(t('auto.iso.certificatesPdfGenerated'));
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleEmployee = (empId) => {
    setSelectedEmployees(prev => 
      prev.includes(empId) 
        ? prev.filter(id => id !== empId)
        : [...prev, empId]
    );
  };

  if (!training) return null;

  const getTypeBadge = (type) => {
    const types = {
      internal: { color: 'bg-blue-100 text-blue-800', label: t('auto.lbl.internal') },
      external: { color: 'bg-green-100 text-green-800', label: t('auto.lbl.external') },
      online: { color: 'bg-purple-100 text-purple-800', label: t('auto.lbl.online') },
      on_the_job: { color: 'bg-orange-100 text-orange-800', label: t('auto.lbl.onTheJob') },
    };
    const tItem = types[type] || types.internal;
    return <Badge className={tItem.color}>{tItem.label}</Badge>;
  };

  const getStatusBadge = (status) => {
    const statuses = {
      planned: { color: 'bg-blue-100 text-blue-800', label: t('auto.lbl.planned') },
      in_progress: { color: 'bg-yellow-100 text-yellow-800', label: t('auto.lbl.inProgress') },
      completed: { color: 'bg-green-100 text-green-800', label: t('auto.lbl.completed') },
      expired: { color: 'bg-red-100 text-red-800', label: t('auto.lbl.expired') },
      cancelled: { color: 'bg-gray-100 text-gray-800', label: t('auto.lbl.cancelled') },
    };
    const s = statuses[status] || statuses.planned;
    return <Badge className={s.color}>{s.label}</Badge>;
  };

  const getParticipantStatusBadge = (status) => {
    const statuses = {
      enrolled: { color: 'bg-gray-100 text-gray-800', label: t('auto.lbl.enrolled') },
      attended: { color: 'bg-blue-100 text-blue-800', label: t('auto.lbl.attended') },
      passed: { color: 'bg-green-100 text-green-800', label: t('auto.lbl.passed') },
      failed: { color: 'bg-red-100 text-red-800', label: t('auto.lbl.failed') },
      excused: { color: 'bg-yellow-100 text-yellow-800', label: t('auto.lbl.excused') },
    };
    const s = statuses[status] || statuses.enrolled;
    return <Badge className={s.color}>{s.label}</Badge>;
  };

  const getEmployeeName = (id) => {
    const emp = employees?.find(e => e.id === id);
    return emp?.name || 'Unknown';
  };

  const getProjectName = (id) => {
    const proj = projects?.find(p => p.id === id);
    return proj?.name || 'Unknown';
  };

  const getTrainerName = () => {
    if (!training.trainer_id) return 'Not assigned';
    return getEmployeeName(training.trainer_id);
  };

  // Calculate completion stats
  const completionStats = {
    total: training.employee_ids?.length || 0,
    passed: 0,
    failed: 0,
    attended: 0,
    enrolled: 0
  };
  
  training.employee_ids?.forEach(empId => {
    const result = participantResults[empId];
    const status = result?.status || 'enrolled';
    if (status === 'passed') completionStats.passed++;
    else if (status === 'failed') completionStats.failed++;
    else if (status === 'attended') completionStats.attended++;
    else completionStats.enrolled++;
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" aria-describedby="training-dialog-description">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-indigo-100">
                <FileText className="h-5 w-5 text-indigo-600" />
              </div>
              <div>
                <DialogTitle>{training.training_number}</DialogTitle>
                <p className="text-sm text-gray-500">{training.title}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {getTypeBadge(training.training_type)}
              {getStatusBadge(training.status)}
            </div>
          </div>
        </DialogHeader>
        <DialogDescription id="training-dialog-description" className="sr-only">
          {t('auto.iso.trainingRecordDetails')}
        </DialogDescription>

        <Tabs defaultValue="details">
          <TabsList className="flex flex-wrap h-auto gap-1 p-1">
            <TabsTrigger value="details" data-testid="tab-details">{t('auto.iso.details')}</TabsTrigger>
            <TabsTrigger value="participants" data-testid="tab-participants">
              Participants ({completionStats.total})
            </TabsTrigger>
            <TabsTrigger value="certification" data-testid="tab-certification">{t('auto.iso.certification')}</TabsTrigger>
          </TabsList>

          <TabsContent value="details" className="space-y-4">
            {isEditing ? (
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium">{t('auto.iso.title2')}</label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value  }))}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">{t('auto.iso.description')}</label>
                  <textarea
                    className="w-full min-h-[80px] p-2 border rounded-md text-sm"
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value  }))}
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">{t('auto.iso.type')}</label>
                    <Select
                      value={formData.training_type}
                      onValueChange={(v) => setFormData(prev => ({ ...prev, training_type: v  }))}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="internal">{t('auto.iso.internal')}</SelectItem>
                        <SelectItem value="external">{t('auto.iso.external')}</SelectItem>
                        <SelectItem value="online">{t('auto.iso.onlineELearning')}</SelectItem>
                        <SelectItem value="on_the_job">{t('auto.iso.onTheJob')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium">{t('auto.iso.provider')}</label>
                    <Input
                      value={formData.provider}
                      onChange={(e) => setFormData(prev => ({ ...prev, provider: e.target.value  }))}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm font-medium">{t('auto.iso.durationHours')}</label>
                    <Input
                      type="number"
                      value={formData.duration_hours}
                      onChange={(e) => setFormData(prev => ({ ...prev, duration_hours: parseFloat(e.target.value) || 1  }))}
                      min="0.5"
                      step="0.5"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">{t('auto.iso.passingScore')}</label>
                    <Input
                      type="number"
                      value={formData.passing_score}
                      onChange={(e) => setFormData(prev => ({ ...prev, passing_score: parseFloat(e.target.value) || 80  }))}
                      min="0"
                      max="100"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">{t('auto.iso.passCriteria')}</label>
                    <Input
                      value={formData.pass_criteria}
                      onChange={(e) => setFormData(prev => ({ ...prev, pass_criteria: e.target.value  }))}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <Card>
                <CardContent className="p-4 space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">{t('auto.iso.scheduledDate')}</p>
                      <p className="font-medium">
                        {training.scheduled_date 
                          ? <DateText value={training.scheduled_date} />
                          : 'Not scheduled'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">{t('auto.iso.completedDate')}</p>
                      <p className="font-medium">
                        {training.completed_date 
                          ? <DateText value={training.completed_date} />
                          : 'Not completed'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">{t('auto.iso.duration')}</p>
                      <p className="font-medium">{training.duration_hours || '-'} hours</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">{t('auto.iso.provider')}</p>
                      <p className="font-medium">{training.provider || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">{t('auto.iso.trainer')}</p>
                      <p className="font-medium">{getTrainerName()}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">{t('auto.iso.passingScore2')}</p>
                      <p className="font-medium">{training.passing_score || 80}%</p>
                    </div>
                  </div>
                  {training.pass_criteria && (
                    <div>
                      <p className="text-sm text-gray-500">{t('auto.iso.passCriteria')}</p>
                      <p className="text-sm">{training.pass_criteria}</p>
                    </div>
                  )}
                  {training.description && (
                    <div>
                      <p className="text-sm text-gray-500">{t('auto.iso.description')}</p>
                      <p className="text-sm">{training.description}</p>
                    </div>
                  )}
                  {training.linked_project_ids?.length > 0 && (
                    <div>
                      <p className="text-sm text-gray-500 mb-2">{t('auto.iso.linkedProjects')}</p>
                      <div className="flex flex-wrap gap-2">
                        {training.linked_project_ids.map(pid => (
                          <Badge key={pid} variant="outline">{getProjectName(pid)}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="participants" className="space-y-4">
            {/* Completion Summary */}
            {completionStats.total > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <Card className="bg-gray-50">
                  <CardContent className="p-3 text-center">
                    <p className="text-2xl font-bold text-gray-700">{completionStats.total}</p>
                    <p className="text-xs text-gray-500">{t('auto.iso.total')}</p>
                  </CardContent>
                </Card>
                <Card className="bg-green-50">
                  <CardContent className="p-3 text-center">
                    <p className="text-2xl font-bold text-green-600">{completionStats.passed}</p>
                    <p className="text-xs text-gray-500">{t('auto.iso.passed')}</p>
                  </CardContent>
                </Card>
                <Card className="bg-blue-50">
                  <CardContent className="p-3 text-center">
                    <p className="text-2xl font-bold text-blue-600">{completionStats.attended}</p>
                    <p className="text-xs text-gray-500">{t('auto.iso.attended')}</p>
                  </CardContent>
                </Card>
                <Card className="bg-red-50">
                  <CardContent className="p-3 text-center">
                    <p className="text-2xl font-bold text-red-600">{completionStats.failed}</p>
                    <p className="text-xs text-gray-500">{t('auto.iso.failed')}</p>
                  </CardContent>
                </Card>
              </div>
            )}

            {isEditing ? (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">{t('auto.iso.selectParticipants')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="border rounded-md p-2 max-h-[300px] overflow-y-auto">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {employees?.map(emp => (
                        <label key={emp.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 p-2 rounded">
                          <input
                            type="checkbox"
                            checked={selectedEmployees.includes(emp.id)}
                            onChange={() => toggleEmployee(emp.id)}
                            className="rounded"
                          />
                          {emp.name}
                        </label>
                      ))}
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">{selectedEmployees.length} participants selected</p>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardHeader className="pb-2 flex flex-row items-center justify-between">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <User className="h-4 w-4" />
                    {t('auto.iso.trainingParticipants')}
                  </CardTitle>
                  {training.employee_ids?.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={handleMarkAllAttended}
                        disabled={isLoading}
                        data-testid="mark-all-attended-btn"
                      >
                        {t('auto.iso.markAllAttended')}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleMarkAllPassed}
                        disabled={isLoading}
                        className="border-green-300 text-green-700 hover:bg-green-50"
                        data-testid="mark-all-passed-btn"
                      >
                        {t('auto.iso.markAllPassed')}
                      </Button>
                    </div>
                  )}
                </CardHeader>
                <CardContent>
                  {training.employee_ids?.length > 0 ? (
                    <div className="space-y-3">
                      {training.employee_ids.map((empId, idx) => {
                        const result = participantResults[empId] || {};
                        const status = result.status || 'enrolled';
                        const score = result.score;
                        
                        return (
                          <div key={idx} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-medium">
                                {getEmployeeName(empId).charAt(0)}
                              </div>
                              <div>
                                <p className="font-medium">{getEmployeeName(empId)}</p>
                                {score !== undefined && score !== null && (
                                  <p className="text-xs text-gray-500">
                                    Score: <span className={score >= (training.passing_score || 80) ? 'text-green-600' : 'text-red-600'}>{score}%</span>
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {getParticipantStatusBadge(status)}
                              <Select
                                value={status}
                                onValueChange={(value) => handleUpdateParticipantStatus(empId, value)}
                                disabled={isUpdatingParticipant === empId}
                              >
                                <SelectTrigger className="w-[120px] h-8 text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="enrolled">{t('auto.iso.enrolled')}</SelectItem>
                                  <SelectItem value="attended">{t('auto.iso.attended')}</SelectItem>
                                  <SelectItem value="passed">{t('auto.iso.passed')}</SelectItem>
                                  <SelectItem value="failed">{t('auto.iso.failed')}</SelectItem>
                                  <SelectItem value="excused">{t('auto.iso.excused')}</SelectItem>
                                </SelectContent>
                              </Select>
                              <Input
                                type="number"
                                placeholder={t('auto.iso.score')}
                                className="w-20 h-8 text-xs"
                                min="0"
                                max="100"
                                defaultValue={score || ''}
                                onBlur={(e) => {
                                  if (e.target.value) {
                                    handleUpdateParticipantStatus(empId, status, e.target.value);
                                  }
                                }}
                              />
                              <Input
                                type="date"
                                className="w-[140px] h-8 text-xs"
                                title={t('auto.iso.completionDateOverridesTrainingWideDate')}
                                value={
                                  result?.completed_date
                                    ? new Date(result.completed_date).toISOString().slice(0, 10)
                                    : ''
                                }
                                onChange={(e) => handleUpdateParticipantCompletedDate(empId, e.target.value)}
                                disabled={isUpdatingParticipant === empId}
                                data-testid={`participant-completion-date-${empId}`}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 text-center py-4">{t('auto.iso.noParticipantsAssigned')}</p>
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="certification" className="space-y-4">
            <Card>
              <CardContent className="p-4 space-y-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">{t('auto.iso.certificationRequired')}</span>
                  <Badge className={training.certification_required ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                    {training.certification_required ? t('auto.iso.yes') : t('auto.iso.no')}
                  </Badge>
                </div>
                {training.certification_required && (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-500">{t('auto.iso.certificationName')}</p>
                        <p className="font-medium">{training.certification_name || '-'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">{t('auto.iso.expiryDate')}</p>
                        <p className={`font-medium ${
                          training.certification_expiry && new Date(training.certification_expiry) < new Date() 
                            ? 'text-red-600' 
                            : ''
                        }`}>
                          {training.certification_expiry 
                            ? <DateText value={training.certification_expiry} />
                            : 'Not set'}
                        </p>
                      </div>
                    </div>
                    
                    {/* Certificate issuance for passed participants */}
                    <div className="border-t pt-4 mt-4">
                      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                        <p className="text-sm font-medium">{t('auto.iso.certificateIssuance')}</p>
                        <div className="flex flex-wrap gap-2">
                          {(() => {
                            const pending = (training.employee_ids || []).filter(empId => {
                              const r = participantResults[empId];
                              return r?.status === 'passed' && !r?.certificate_issued;
                            }).length;
                            return pending > 0 ? (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={handleIssueAllCertificates}
                                disabled={isLoading}
                                className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                                data-testid="issue-all-certificates-btn"
                              >
                                Issue All Certificates ({pending})
                              </Button>
                            ) : null;
                          })()}
                          {(() => {
                            const passedCount = (training.employee_ids || []).filter(empId => {
                              const r = participantResults[empId];
                              return r?.status === 'passed';
                            }).length;
                            return passedCount > 0 ? (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={handleGenerateCertificatesPdf}
                                disabled={isLoading}
                                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                                data-testid="generate-certificates-pdf-btn"
                              >
                                Generate Certificates PDF ({passedCount})
                              </Button>
                            ) : null;
                          })()}
                        </div>
                      </div>
                      {training.employee_ids?.filter(empId => {
                        const result = participantResults[empId];
                        return result?.status === 'passed';
                      }).length > 0 ? (
                        <div className="space-y-2">
                          {training.employee_ids.filter(empId => {
                            const result = participantResults[empId];
                            return result?.status === 'passed';
                          }).map(empId => {
                            const result = participantResults[empId] || {};
                            return (
                              <div key={empId} className="flex items-center justify-between p-2 border rounded">
                                <span className="font-medium">{getEmployeeName(empId)}</span>
                                {result.certificate_issued ? (
                                  <Badge className="bg-green-100 text-green-800">{t('auto.iso.certificateIssued2')}</Badge>
                                ) : (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleIssueCertificate(empId)}
                                    disabled={isUpdatingParticipant === empId}
                                   data-testid="btn-issue-certificate">
                                    {t('auto.iso.issueCertificate')}
                                  </Button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500">{t('auto.iso.noParticipantsHavePassedYet')}</p>
                      )}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <DialogFooter className="flex justify-between">
          <div></div>
          <div className="flex gap-2">
            {isEditing ? (
              <>
                <Button variant="outline" onClick={() => setIsEditing(false)} data-testid="dialog-cancel-btn">{t('auto.iso.cancel')}</Button>
                <Button onClick={handleSave} disabled={isLoading} data-testid="dialog-save-btn">
                  <Save className="h-4 w-4 mr-2" />
                  {isLoading ? t('auto.iso.saving') : t('auto.iso.save')}
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => setIsEditing(true)} data-testid="dialog-isodetaildialogs-outline-btn">
                  <Edit className="h-4 w-4 mr-2" />
                  {t('auto.iso.edit')}
                </Button>
                <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="dialog-close-btn">{t('auto.iso.close')}</Button>
              </>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default { DocumentDetailDialog, NCRDetailDialog, CAPADetailDialog, TrainingDetailDialog };
